import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    RegionDTO,
    useDeleteRegionApi,
    useSaveRegionApi,
    useSearchRegionApi,
} from '../../../api/region-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveRegionModal } from './save-region-modal';

export function RegionApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<RegionDTO | undefined>();
    const regionInit = useSearchRegionApi();
    const saveRegion = useSaveRegionApi();
    const deleteRegion = useDeleteRegionApi();

    const { result, error, busy, initialized, initializing } = regionInit;

    const columns: IBasicTableColumn<RegionDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
            },
            {
                Header: <T>name</T>,
                name: 'name',
                width: 120,
            },
            {
                Header: <T>description</T>,
                name: 'description',
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        regionInit.post(null, {});
    }, [initialized, initializing, error, regionInit]);

    const handleDelete = (region: RegionDTO) => {
        bd.msgbox(
            translate('delete-region'),
            translate('confirm-delete-region'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteRegion.remove(
                            region,
                            () => {
                                regionInit.updateResult({
                                    ...result,
                                    regions: result.regions.filter(b => b.id !== region.id),
                                });
                                notify.dark(translate('selected-region-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedRegion(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (region: RegionDTO) => {
        saveRegion.post(
            null,
            region,
            res => {
                if (selectedRegion) {
                    regionInit.updateResult({
                        ...result,
                        regions: result.regions.map(b => (b.id === region.id ? res.region : b)),
                    });
                } else {
                    regionInit.updateResult({
                        ...result,
                        regions: [res.region, ...result.regions],
                    });
                }

                setShowModal(false);
                setSelectedRegion(undefined);
                notify.success(
                    selectedRegion
                        ? translate('selected-region-updated-successfully')
                        : translate('new-region-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => regionInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="regions"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-region</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedRegion(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.regions}
                        onRowClick={({ row }) => {
                            setSelectedRegion(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveRegionModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedRegion(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedRegion={selectedRegion}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
